import { useEffect } from "react";
import { useWatch } from "react-hook-form";
import { getPointTypesList } from "tm-shared/api";
import { validationMessage } from "tm-shared/utils";

import { useAsyncOptions } from "@/hooks";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { RHFAutocomplete } from "@/shared/forms/RHF/RHFAutocomplete";
import { setPointCreatePageOptionsCache } from "@/store/slices/ui";
import { NewPointFormValues } from "@/types/form";

import { FieldName } from "../CreatePointForm.constants";

interface PointTypeSectionProps {
    activeMap: Record<FieldName, boolean>;
    resetBelow: (field: FieldName) => void;
}

export const PointTypeSection = ({
    activeMap,
    resetBelow,
}: PointTypeSectionProps) => {
    // Store data
    const dispatch = useAppDispatch();
    const { optionCache } = useAppSelector((s) => s.ui.pointCreatePage);

    // Form values
    const pointType = useWatch<NewPointFormValues, "pointType">({
        name: "pointType",
    });

    // Point type options
    const { options: pointTypesOptions, loading: loadingPointTypes } =
        useAsyncOptions({
            fetchFn: (signal) =>
                getPointTypesList({ axiosOverrides: { signal } }),
            selectData: (response) => Object.entries(response.result),
            mapToOption: ([value, label]) => ({ label, value }),
            dependencies: [],
            enabled: activeMap.pointType && !pointType,
        });

    useEffect(() => {
        if (pointTypesOptions?.length) {
            dispatch(
                setPointCreatePageOptionsCache({
                    fieldName: "pointType",
                    options: pointTypesOptions,
                }),
            );
        }
    }, [dispatch, pointTypesOptions]);

    // Render
    return (
        <RHFAutocomplete
            name="pointType"
            options={optionCache?.pointType ?? []}
            label="Тип ТТ"
            allowClear={false}
            disabled={!activeMap.pointType}
            onAfterChange={() => resetBelow("pointType")}
            rules={{
                required: validationMessage.required,
            }}
            loading={loadingPointTypes}
            selectProps={{
                size: "large",
            }}
        />
    );
};
